<template>
  <div :class="['db-app', 'db-layout-grid-wrapper', 'db-rfp', { 'db-rfp-iframe': isIframe() }]">
    <div class="db-rfp-container db-tw-py-11">
      <RfpConfigurationWarning
        v-if="showDebug"
        class="db-tw-mb-10 md:db-tw-mx-auto"
        :stepsSchema="steps"
        :defaultData="defaultData"
      />

      <!-- Loading -->
      <div
        v-if="status === 'loading'"
        class="db-tw-flex db-tw-flex-col db-tw-items-center db-tw-justify-center"
      >
        <DbSpinner />
        <h2 class="db-tw-my-6 db-title-3">
          {{ $t('Your request is being sent') }}
        </h2>
        <p>{{ $t('Thank you for your patience') }}</p>
      </div>

      <RfpFormWizard
        v-if="status === 'form'"
        :stepsSchema="steps"
        :defaultData="defaultData"
        v-on:submit-form="sendForm"
      />

      <div
        v-if="status === 'success'"
        class="db-tw-text-center"
      >
        <h2 class="db-tw-mb-6 db-title-3">
          {{ $t('Done! Request sent successfully.') }}
        </h2>
        <p class="db-tw-mb-4.5">
          {{ $t('You’ll receive an email confirmation.') }}
        </p>
        <a
          v-if="successScreen?.buttonUrl"
          :href="successScreen?.buttonUrl"
          :target="successScreen?.buttonNewTab ? '_blank' : ''"
          type="button"
          class="db-tw-mt-8 db-button-large db-button-primary"
        >
          {{ $t('Go to Homepage') }}
        </a>
      </div>
      <div
        v-if="status === 'error'"
        class="db-tw-text-center"
      >
        <IconDbWarning
          class="db-tw-10"
          width="40"
          height="40"
        />
        <h2 class="db-tw-mb-6 db-title-2">
          {{ $t('There was an error submitting the form') }}
        </h2>
        <button
          v-if="displaySubmitAgain"
          class="db-tw-mt-8 db-button-large db-button-primary"
          v-on:click.stop.prevent="retry()"
        >
          {{ $t('Submit again') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { RfpFormStep } from './types/RfpFormSteps'
import type { RfpFormData } from './types/RfpFormData'
import { HAS_DEBUG_MODE } from '@/config'
import { useGtm } from '@/composables/features/useGtm'

defineProps<{
  steps: RfpFormStep[]
  defaultData: RfpFormData
  successScreen?: {
    buttonUrl: string
    buttonNewTab: boolean
  }
}>()

const showDebug = HAS_DEBUG_MODE

/* Use for error page to save data and submit again the form */
const saveFormdata = ref<RfpFormData | undefined>(undefined)
const displaySubmitAgain = ref(true) /* Display submit again button by default */

const status = ref<'form' | 'loading' | 'success' | 'error'>('form')

const { formEnd } = useGtm()

/** Send form to API */
function sendForm(formData: RfpFormData) {
  status.value = 'loading' // loader
  saveFormdata.value = formData

  /* We load the API only when really needed */
  import('./services/MeetingBrokerApi')
    .then(({ postRfp }) => {
      postRfp(formData)
        .then(() => {
          formEnd('contact', 'rfp', 'gaEventDelphiDirect', 'form_confirmation')
          status.value = 'success'
        })
        .catch((error) => {
          status.value = 'error'
          console.error(error)
        })
    })
    .catch((error) => {
      status.value = 'error'
      console.error(error)
    })
}

function isIframe() {
  return window.location !== window.parent.location
}

/**
 * @function retry - on click on submit again button on error page
 * Have the possibility to send again the form with the same formData once
 */
function retry() {
  if (saveFormdata.value !== undefined) {
    sendForm(saveFormdata.value)
  }

  displaySubmitAgain.value = false
}
</script>
<style>
.db-app .db-rfp-container .db-input,
.db-app .db-rfp-container .db-textarea {
  color: var(--db_input_text_color);
}

.db-rfp-iframe {
  --db_body_text_color: var(--db_rfp_iframe_body_text_color);
  --db-color-text-input-field-label-error: var(--db_rfp_iframe_inline_error_message_text_color);
  --db-color-border-input-field-error: var(--db_rfp_iframe_inline_error_message_text_color);
  --db-color-text-input-field-hint: var(--db_rfp_iframe_body_text_color);
  --db-color-text-rfp-stepper: var(--db_rfp_iframe_body_text_color);
  --db_form_border_color: var(--db_rfp_iframe_body_text_color);
  --db-box-shadow-rfp-stepper-counter: 0 0 0 2px var(--db_rfp_iframe_body_text_color) inset;
  --db-color-background-rfp-stepper-counter: var(--db_rfp_iframe_background_color);
  --db_rfp_background_color: var(--db_rfp_iframe_background_color);
}
</style>
